/* Copyright (c) 2020-present CloudIO Inc */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ComponentManager,
  DevProps,
  getManager,
  ItemID,
  Platform,
} from 'componentregistry';
import {
  useEvent,
  useStateXAtomValue,
  useStateXGetter,
  useStateXRefSetter,
  useStateXSelector,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { RefCache } from 'designer/ComponentHoverTip';
import type { HTMLMotionProps } from 'framer-motion';
import useAnimationFrame from 'hooks/useAnimationFrame';
import { CSSProperties, useCallback, useEffect } from 'react';
import { componentTipAtom } from 'utils/state';
import { yellow } from '@mui/material/colors';
import {
  useCollectionRowIndex,
  useRowIndex,
  useStaticRuntime,
} from 'common/Context';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { NOT_DEFINED } from 'utils/isEmpty';
import { Theme } from '@mui/material';
import { useStoreCurrentRecordId } from '../store/useAppStore';
import { IORef, useIORef } from './useIORef';
import { activeItemSelector } from './activeItemSelector';
import {
  activeItemIdAtom,
  activeItemRefCounterAtom,
  activeItemRefPath,
  draggingAtom,
  dragItemAtom,
  dragSourceOffsetAtom,
  pageAtom,
} from './atoms';
import Overlay from './Overlay';
import { visibleChildrenSelector } from './state';
import useDesignerLite from './useDesignerLite';
import useDnD from './useDnD';
import useUser from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  dragging: {
    pointerEvents: 'none',
    cursor: 'move',
  },
  normal: {
    position: 'relative',
    zIndex: 'unset',
    '&.absolute': {
      position: 'absolute',
    },
    '&.position-unset': {
      position: 'unset',
    },
  },
  proxy: {
    pointerEvents: 'none',
    opacity: 0.3,
    '&::before': {
      display: 'flex',
      flex: 1,
      content: "' '",
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      // backgroundColor: theme.palette.background.paper,
      // backgroundImage:
      //   'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="yellow"><rect x="20" width="20" height="20"/><rect y="20" width="20" height="20"/></svg>\')',
      // backgroundSize: '20px 20px',
      border: `2px dashed ${
        theme.palette.mode === 'dark' ? yellow[200] : theme.palette.error.dark
      }`,
      opacity: 1,
    },
  },
  empty: {
    position: 'relative',
    minHeight: '1em',
    minWidth: '1em',
    '&::before': {
      display: 'flex',
      flex: 1,
      content: ({ name }: { name: string }) => `'${name}'`,
      alignContent: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      fill: theme.palette.background.default,
      color: theme.palette.warning.main,
      backgroundColor: theme.palette.background.paper,
      backgroundImage: `url('data:image/svg+xml,\
               <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="${
                 theme.palette.mode === 'dark'
                   ? 'rgb(48,48,48)'
                   : 'rgb(221,221,221)'
               }">\
               <rect x="20" width="20" height="20"/><rect y="20" width="20" height="20"/></svg>')`,
      backgroundSize: '20px 20px',
      border: `1px solid ${theme.palette.warning.light}`,
      fontWeight: 600,
      fontSize: '1em',
      lineHeight: '1em',
      minHeight: '3em',
      minWidth: '10em',
    },
  },
}));

interface MotionProps extends HTMLMotionProps<'div'> {
  key: string;
  style?: CSSProperties;
}

export default function useDesigner(
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
  platform: Platform,
): {
  Overlay: React.FunctionComponent<DevProps> | null;
  devProps: DevProps;
  dragging: boolean;
  mgr: ComponentManager<any>;
  motionProps: MotionProps;
  ref: IORef<HTMLElement>;
} {
  const ref = useIORef<HTMLElement>();
  ref.itemId = itemId;
  const rowIndex = useRowIndex() ?? -1;
  const collectionRowIndex = useCollectionRowIndex();
  const store = useCurrentDataStore();
  const id = useStoreCurrentRecordId(store);

  const dragItemId = useStateXAtomValue(dragItemAtom, {
    shouldComponentUpdate: (value, oldValue) => {
      return value === itemId || oldValue === itemId;
    },
  });
  const setRef = useStateXRefSetter();
  const set = useStateXSetter();
  const get = useStateXGetter();

  const setActiveRef = useEvent((activeItemId: string) => {
    if (activeItemId === itemId) {
      if (
        !(store && id) ||
        rowIndex === -1 ||
        store.recordsOrder().indexOf(id) === rowIndex
      ) {
        setRef(activeItemRefPath, ref);
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const offset = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
          };
          set(dragSourceOffsetAtom, offset);
          set(activeItemRefCounterAtom, (c) => c + 1);
        }
      }
    }
  });

  useStateXSelector(activeItemSelector, {
    params: { appUid, treename: 'designer', pageId },
    props: platform,
    shouldComponentUpdate: (value) => {
      // avoid unnecessary re-render
      setActiveRef(value.itemId);
      return false;
    },
  });
  const user = useUser();
  const showComponentTip = useStateXAtomValue(componentTipAtom, {
    params: { userName: user.userName },
  });

  // useWhyDidYouUpdate('useDesigner', {
  //   dragItemId,
  //   showComponentTip,
  //   activeItemId,
  // });

  useEffect(() => {
    RefCache[`${pageId}-${itemId}-${rowIndex}`] = ref;
    const activeItemId =
      get(activeItemIdAtom, {
        params: { appUid, pageId, treename: 'designer' },
      }) || 'root';
    setActiveRef(activeItemId);
    return () => {
      delete RefCache[`${pageId}-${itemId}-${rowIndex}`];
    };
  }, [appUid, get, itemId, pageId, ref, rowIndex, setActiveRef]);

  const update = useAnimationFrame(() => {
    const a = get(activeItemSelector, {
      params: { appUid, treename: 'designer', pageId },
      props: platform,
    });
    if (a.itemId === itemId && showComponentTip) {
      set(activeItemRefCounterAtom, (c) => c + 1);
    }
    if (ref.current && dragItemId === itemId) {
      // update dragSourceOffsetAtom since the sourceItem has moved, and the
      // original source cursor position should not be used for further calculation
      const rect = ref.current.getBoundingClientRect();
      const offset = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      set(dragSourceOffsetAtom, offset);
    }
  });

  const page = get(pageAtom, { params: { appUid, pageId } });
  if (!page) {
    throw Error(`Missing page w/ id ${pageId}`);
  }
  const item = page[itemId];
  if (!item) {
    throw Error(`Missing item w/ id ${itemId}`);
  }
  const mgr = getManager(item.type) as ComponentManager<any>;
  const classes = useStyles({ name: mgr.name });

  useDnD({ appUid, pageId, item, ref });

  const onLayoutAnimationComplete = update;

  const { children } = useStateXSelector(visibleChildrenSelector, {
    params: { appUid, pageId, itemId, rowIndex: `r-${rowIndex}` },
    props: { platform, rowIndex, dsAlias: store?.alias ?? NOT_DEFINED },
  });

  const className = clsx(
    !mgr.canHaveChildren() ||
      children.length ||
      !mgr.showEmptyPlaceholderInDesignerView
      ? null
      : classes.empty,
    classes.normal,
    dragItemId === itemId ? classes.proxy : null,
  );

  const layoutId = `${itemId}:${pageId}:${rowIndex}:${collectionRowIndex}`;

  const { onClick, onContextMenu, onMouseEnter, onMouseLeave } =
    useDesignerLite(appUid, pageId, itemId);

  const transformTemplate = useCallback(
    (_: unknown, template: string): string => {
      if (
        !get(draggingAtom) &&
        template &&
        template.startsWith('translate3d')
      ) {
        return 'translate3d(0px, 0px, 0) scale(1, 1)';
      }
      return template;
    },
    [get],
  );

  const staticRuntime = useStaticRuntime();

  return {
    Overlay,
    dragging: dragItemId === itemId,
    mgr,
    motionProps: {
      className,
      key: layoutId,
      layout: 'position' as const,
      layoutId,
      onLayoutAnimationComplete,
      style: mgr.devStyles,
      initial: false,
      transformTemplate,
    },
    devProps: {
      onClick,
      onMouseEnter,
      onMouseLeave,
      onContextMenu: staticRuntime ? undefined : onContextMenu,
    },
    ref,
  };
}
